import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { FaArrowLeft, FaPlus, FaTimes, FaUpload } from 'react-icons/fa';
import { useToast } from '../../context/ToastContext';
import { LoadingState } from '../../components/Loading';
import './ProductForm.css';

const ProductForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const isEdit = Boolean(id);

  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(isEdit);
  const [existingImages, setExistingImages] = useState([]);
  const [imageFiles, setImageFiles] = useState([]);
  const [imagePreviews, setImagePreviews] = useState([]);
  const [formData, setFormData] = useState({
    name: '',
    slug: '',
    description: '',
    price: '',
    comparePrice: '',
    category: '',
    stock: '',
    sku: '',
    featured: false,
    isActive: true,
    tags: '',
    specifications: [{ key: '', value: '' }],
  });

  useEffect(() => {
    loadCategories();
    if (isEdit) loadProduct();
  }, [id]);

  const loadCategories = async () => {
    try {
      const res = await axios.get('/api/categories');
      setCategories(res.data);
    } catch (e) { console.error(e); }
  };

  const loadProduct = async () => {
    try {
      const res = await axios.get(`/api/products/${id}`);
      const p = res.data;
      setFormData({
        name: p.name,
        slug: p.slug,
        description: p.description,
        price: p.price,
        comparePrice: p.comparePrice || '',
        category: p.category?._id || '',
        stock: p.stock,
        sku: p.sku || '',
        featured: p.featured,
        isActive: p.isActive,
        tags: p.tags?.join(', ') || '',
        specifications: p.specifications?.length > 0 ? p.specifications : [{ key: '', value: '' }],
      });
      setExistingImages(p.images || []);
    } catch (error) {
      toast.error('Could not load product');
    } finally {
      setFetching(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));

    if (name === 'name' && !isEdit) {
      const slug = value.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
      setFormData((prev) => ({ ...prev, slug }));
    }
  };

  const handleSpecChange = (index, field, value) => {
    const newSpecs = [...formData.specifications];
    newSpecs[index][field] = value;
    setFormData({ ...formData, specifications: newSpecs });
  };

  const addSpec = () => {
    setFormData({ ...formData, specifications: [...formData.specifications, { key: '', value: '' }] });
  };

  const removeSpec = (index) => {
    setFormData({ ...formData, specifications: formData.specifications.filter((_, i) => i !== index) });
  };

  const handleImageChange = (e) => {
    const files = Array.from(e.target.files);
    setImageFiles(files);
    setImagePreviews(files.map((f) => URL.createObjectURL(f)));
  };

  const removePreview = (index) => {
    setImageFiles(imageFiles.filter((_, i) => i !== index));
    setImagePreviews(imagePreviews.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const data = new FormData();
      data.append('name', formData.name);
      data.append('slug', formData.slug);
      data.append('description', formData.description);
      data.append('price', formData.price);
      if (formData.comparePrice) data.append('comparePrice', formData.comparePrice);
      data.append('category', formData.category);
      data.append('stock', formData.stock);
      if (formData.sku) data.append('sku', formData.sku);
      data.append('featured', formData.featured);
      data.append('isActive', formData.isActive);

      const tags = formData.tags.split(',').map((t) => t.trim()).filter(Boolean);
      data.append('tags', JSON.stringify(tags));

      const specs = formData.specifications.filter((s) => s.key && s.value);
      data.append('specifications', JSON.stringify(specs));

      imageFiles.forEach((image) => data.append('images', image));

      if (isEdit) {
        await axios.put(`/api/products/${id}`, data);
        toast.success('Product updated');
      } else {
        await axios.post('/api/products', data);
        toast.success('Product created');
      }

      navigate('/admin/products');
    } catch (error) {
      toast.error(error.response?.data?.message || 'Save failed');
    } finally {
      setLoading(false);
    }
  };

  if (fetching) return <LoadingState message="Loading product..." />;

  return (
    <div className="product-form">
      <div className="admin-header">
        <div>
          <Link to="/admin/products" className="back-link">
            <FaArrowLeft /> Products
          </Link>
          <h1>{isEdit ? 'Edit product' : 'Add new product'}</h1>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="product-form-grid">
        <div className="product-form-main">
          {/* Basic */}
          <div className="card">
            <div className="card-header"><h2>Basic information</h2></div>
            <div className="form-group">
              <label>Product name *</label>
              <input type="text" name="name" required value={formData.name} onChange={handleChange} />
            </div>
            <div className="form-group">
              <label>Slug *</label>
              <input type="text" name="slug" required value={formData.slug} onChange={handleChange} />
              <small>URL-friendly version, used in product links</small>
            </div>
            <div className="form-group">
              <label>Description *</label>
              <textarea name="description" required rows="5" value={formData.description} onChange={handleChange} />
            </div>
          </div>

          {/* Images */}
          <div className="card">
            <div className="card-header"><h2>Images</h2></div>

            {existingImages.length > 0 && (
              <>
                <label>Current images</label>
                <div className="image-preview-grid">
                  {existingImages.map((img, i) => (
                    <div key={i} className="image-preview">
                      <img src={img.url} alt="" />
                    </div>
                  ))}
                </div>
                <small style={{ display: 'block', marginTop: 8, marginBottom: 16 }}>
                  Uploading new images will replace these
                </small>
              </>
            )}

            <label htmlFor="images" className="upload-area">
              <FaUpload />
              <strong>Click to upload</strong>
              <small>PNG, JPG, GIF up to 5MB · max 5 images</small>
              <input
                id="images"
                type="file"
                accept="image/*"
                multiple
                onChange={handleImageChange}
                style={{ display: 'none' }}
              />
            </label>

            {imagePreviews.length > 0 && (
              <>
                <label style={{ marginTop: 20 }}>Preview</label>
                <div className="image-preview-grid">
                  {imagePreviews.map((src, i) => (
                    <div key={i} className="image-preview">
                      <img src={src} alt="" />
                      <button
                        type="button"
                        onClick={() => removePreview(i)}
                        className="remove-image"
                      >
                        <FaTimes />
                      </button>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>

          {/* Specs */}
          <div className="card">
            <div className="card-header"><h2>Specifications</h2></div>
            <div className="specs-list">
              {formData.specifications.map((spec, index) => (
                <div key={index} className="spec-row">
                  <input
                    type="text"
                    placeholder="Property name"
                    value={spec.key}
                    onChange={(e) => handleSpecChange(index, 'key', e.target.value)}
                  />
                  <input
                    type="text"
                    placeholder="Value"
                    value={spec.value}
                    onChange={(e) => handleSpecChange(index, 'value', e.target.value)}
                  />
                  <button
                    type="button"
                    onClick={() => removeSpec(index)}
                    className="btn-icon btn-danger"
                  >
                    <FaTimes />
                  </button>
                </div>
              ))}
            </div>
            <button type="button" onClick={addSpec} className="btn btn-secondary btn-sm" style={{ marginTop: 12 }}>
              <FaPlus /> Add specification
            </button>
          </div>
        </div>

        <div className="product-form-side">
          {/* Pricing */}
          <div className="card">
            <div className="card-header"><h2>Pricing</h2></div>
            <div className="form-group">
              <label>Price *</label>
              <input
                type="number" name="price" required
                value={formData.price} onChange={handleChange}
                step="0.01" min="0"
              />
            </div>
            <div className="form-group">
              <label>Compare-at price</label>
              <input
                type="number" name="comparePrice"
                value={formData.comparePrice} onChange={handleChange}
                step="0.01" min="0"
              />
              <small>Original price for showing discounts</small>
            </div>
          </div>

          {/* Inventory */}
          <div className="card">
            <div className="card-header"><h2>Inventory</h2></div>
            <div className="form-group">
              <label>Stock quantity *</label>
              <input
                type="number" name="stock" required
                value={formData.stock} onChange={handleChange} min="0"
              />
            </div>
            <div className="form-group">
              <label>SKU</label>
              <input type="text" name="sku" value={formData.sku} onChange={handleChange} />
            </div>
          </div>

          {/* Organization */}
          <div className="card">
            <div className="card-header"><h2>Organization</h2></div>
            <div className="form-group">
              <label>Category *</label>
              <select name="category" required value={formData.category} onChange={handleChange}>
                <option value="">Select category</option>
                {categories.map((c) => (
                  <option key={c._id} value={c._id}>{c.name}</option>
                ))}
              </select>
              {categories.length === 0 && (
                <small style={{ color: 'var(--warning-700)' }}>
                  No categories yet. <Link to="/admin/categories">Create one</Link> first.
                </small>
              )}
            </div>
            <div className="form-group">
              <label>Tags</label>
              <input
                type="text" name="tags"
                value={formData.tags} onChange={handleChange}
                placeholder="electronics, wireless, audio"
              />
              <small>Comma-separated</small>
            </div>
          </div>

          {/* Visibility */}
          <div className="card">
            <div className="card-header"><h2>Visibility</h2></div>
            <label className="checkbox-row">
              <input type="checkbox" name="featured" checked={formData.featured} onChange={handleChange} />
              <div>
                <strong>Featured product</strong>
                <small>Show on homepage and featured collection</small>
              </div>
            </label>
            <label className="checkbox-row">
              <input type="checkbox" name="isActive" checked={formData.isActive} onChange={handleChange} />
              <div>
                <strong>Active</strong>
                <small>Available for purchase</small>
              </div>
            </label>
          </div>
        </div>

        {/* Sticky form actions */}
        <div className="form-actions-sticky">
          <button
            type="button"
            onClick={() => navigate('/admin/products')}
            className="btn btn-secondary"
          >
            Cancel
          </button>
          <button type="submit" className="btn btn-primary" disabled={loading}>
            {loading ? 'Saving...' : isEdit ? 'Update product' : 'Create product'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default ProductForm;
